/**
 * Web mirror of shared/payPeriodGrid.js, kept identical by
 * src/test/utils/payPeriodGridParity.test.js. An organization's pay period grid.
 *
 * Each organization states its own pay period in `organization.settings.payPeriod`:
 *
 *   { type: 'biweekly', anchor: 'YYYY-MM-DD' }   every 14 days, a period starting on `anchor`
 *   { type: 'semimonthly' }                      1st-15th, then 16th-last day of the month
 *
 * No organization's dates live in code. An organization without the setting is
 * not configured, and callers show nothing rather than borrow someone else's
 * cadence.
 *
 * Every date here is a calendar day ('YYYY-MM-DD'). Periods are numbered so
 * "the previous period" is index - 1 for either type:
 *   biweekly:    floor(days since anchor / 14)
 *   semimonthly: year * 24 + month * 2 + (day > 15 ? 1 : 0)   (month 0-11)
 */
const TYPES = Object.freeze(['biweekly', 'semimonthly']);
const MS_PER_DAY = 86400000;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const toMs = (day) => Date.parse(`${day}T12:00:00Z`);
const fromMs = (ms) => new Date(ms).toISOString().slice(0, 10);
const isDay = (value) => typeof value === 'string' && DAY.test(value) && !Number.isNaN(toMs(value))
  && fromMs(toMs(value)) === value;

/** Calendar day of a 'YYYY-MM-DD' string, an ISO string, or a Date (its UTC date). */
function dayKey(value) {
  if (typeof value === 'string' && DAY.test(value.slice(0, 10))) return value.slice(0, 10);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error(`Not a date: ${value}`);
  return d.toISOString().slice(0, 10);
}

const addDays = (day, n) => fromMs(toMs(day) + n * MS_PER_DAY);
const lastDayOfMonth = (year, month0) => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
const pad = (n) => String(n).padStart(2, '0');

/**
 * Check a submitted setting.
 * @returns {{ ok: true, value: object } | { ok: false, error: string }}
 */
function validatePayPeriodSetting(input) {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Choose a pay period type.' };
  if (input.type === 'semimonthly') return { ok: true, value: { type: 'semimonthly' } };
  if (input.type === 'biweekly') {
    if (!isDay(input.anchor)) return { ok: false, error: 'Enter the date one pay period starts (YYYY-MM-DD).' };
    return { ok: true, value: { type: 'biweekly', anchor: input.anchor } };
  }
  return { ok: false, error: `Pay period type must be one of: ${TYPES.join(', ')}.` };
}

/**
 * The organization's grid from its settings JSON.
 * @returns {{ configured: true, type: 'biweekly', anchor: string } | { configured: true, type: 'semimonthly' } | { configured: false }}
 */
function resolvePayPeriodGrid(orgSettings) {
  const checked = validatePayPeriodSetting(orgSettings?.payPeriod);
  return checked.ok ? { configured: true, ...checked.value } : { configured: false };
}

function requireGrid(grid) {
  if (!grid || !grid.configured) throw new Error('Pay period is not configured for this organization');
  return grid;
}

/** @returns {number} the index of the period holding `day` */
function periodIndexFor(day, grid) {
  requireGrid(grid);
  const d = dayKey(day);
  if (grid.type === 'semimonthly') {
    const [y, m, dd] = d.split('-').map(Number);
    return y * 24 + (m - 1) * 2 + (dd > 15 ? 1 : 0);
  }
  return Math.floor(Math.round((toMs(d) - toMs(grid.anchor)) / MS_PER_DAY) / 14);
}

/** @returns {{ index: number, start: string, end: string, days: number }} */
function periodForIndex(index, grid) {
  requireGrid(grid);
  if (grid.type === 'semimonthly') {
    const year = Math.floor(index / 24);
    const month0 = Math.floor((index - year * 24) / 2);
    const second = index - year * 24 - month0 * 2 === 1;
    const startDay = second ? 16 : 1;
    const endDay = second ? lastDayOfMonth(year, month0) : 15;
    const prefix = `${year}-${pad(month0 + 1)}`;
    return { index, start: `${prefix}-${pad(startDay)}`, end: `${prefix}-${pad(endDay)}`, days: endDay - startDay + 1 };
  }
  const start = addDays(grid.anchor, index * 14);
  return { index, start, end: addDays(start, 13), days: 14 };
}

function periodForDay(day, grid) {
  return periodForIndex(periodIndexFor(day, grid), grid);
}

/** The period `offset` periods from the one holding `day`: 0 current, -1 previous. */
function periodByOffset(day, offset, grid) {
  return periodForIndex(periodIndexFor(day, grid) + offset, grid);
}

/** Short wording for settings and empty states. */
function describePayPeriodGrid(grid) {
  if (!grid || !grid.configured) return 'Not set up';
  if (grid.type === 'semimonthly') return 'Twice a month (1st to 15th, 16th to end of month)';
  const weekday = new Date(toMs(grid.anchor)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  return `Every two weeks, ${weekday} to ${new Date(toMs(addDays(grid.anchor, 13))).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })}`;
}

export {
  TYPES as PAY_PERIOD_TYPES,
  dayKey,
  addDays,
  validatePayPeriodSetting,
  resolvePayPeriodGrid,
  periodIndexFor,
  periodForIndex,
  periodForDay,
  periodByOffset,
  describePayPeriodGrid,
};
